import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, filter, map, Subject } from 'rxjs';
import { ActiveInspectionContextService } from './active-inspection-context.service';
import { UniversalSearchApiService, WorkspaceSearchHit } from './universal-search-api.service';
import { UniversalSearchAvailabilityService } from './universal-search-availability.service';
import { groupUniversalSearchResults, isRemoteSearchQuery, LOCAL_SEARCH_MIN_QUERY_LENGTH, UniversalSearchGroup, UniversalSearchResult, UniversalSearchStatus } from './universal-search.models';

const SEARCH_DEBOUNCE_MS = 200;
const MAX_RECENTS = 8;
const RECENTS_STORAGE_PREFIX = 'cip.universalSearch.recents';

export interface UniversalSearchQuickLink {
  label: string;
  commands: unknown[];
}

/**
 * Owns the Universal Search overlay: its open state, the debounced query, and the merge of backend
 * hits with the contents of the inspection the user has open.
 */
@Injectable({
  providedIn: 'root',
})
export class UniversalSearchService {
  private api = inject(UniversalSearchApiService);
  private activeInspectionContextService = inject(ActiveInspectionContextService);
  private availabilityService = inject(UniversalSearchAvailabilityService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private readonly routeUrl = signal(this.router.url);

  public readonly isOpen = signal(false);
  public readonly query = signal('');
  public readonly status = signal<UniversalSearchStatus>('idle');
  public readonly truncated = signal(false);
  public readonly recents = signal<UniversalSearchResult[]>([]);
  public readonly currentWorkspaceId = computed(() => this.workspaceIdFromUrl(this.routeUrl()));
  public readonly isAvailable = computed(() => this.currentWorkspaceId() !== null);
  public readonly hasActiveInspection = computed(() => this.activeInspectionContextService.currentSnapshot()?.workspaceId === this.currentWorkspaceId());

  private readonly workspaceResults = signal<UniversalSearchResult[]>([]);
  private readonly queryInput = new Subject<string>();

  /**
   * Guards against a slow response overwriting the results of a newer query.
   */
  private latestRequestId = 0;
  private recentsRequestId = 0;

  public readonly activeInspectionTitle = computed(() => this.activeInspectionContextService.titleForWorkspace(this.currentWorkspaceId()));

  public readonly groups = computed<UniversalSearchGroup[]>(() => {
    const query = this.query().trim();
    if (query.length < LOCAL_SEARCH_MIN_QUERY_LENGTH) return [];
    return groupUniversalSearchResults([...this.activeInspectionContextService.search(query, this.currentWorkspaceId()), ...this.workspaceResults()]);
  });

  public readonly results = computed<UniversalSearchResult[]>(() => this.groups().flatMap((group) => group.results));

  public readonly quickLinks = computed<UniversalSearchQuickLink[]>(() => {
    const workspaceId = this.currentWorkspaceId();
    if (!workspaceId) return [];
    return [
      { label: 'Folders', commands: ['/workspace', workspaceId, 'folders'] },
      { label: 'Clients', commands: ['/workspace', workspaceId, 'manage', 'clients'] },
      { label: 'Sites', commands: ['/workspace', workspaceId, 'manage', 'sites'] },
      { label: 'Templates', commands: ['/workspace', workspaceId, 'manage', 'templates'] },
      { label: 'Members', commands: ['/workspace', workspaceId, 'manage', 'members'] },
    ];
  });

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => this.handleNavigation(event.urlAfterRedirects));

    this.queryInput
      .pipe(
        map((query) => query.trim()),
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((query) => void this.searchWorkspace(query));
  }

  public open(): void {
    if (!this.isAvailable()) return;
    this.isOpen.set(true);
    void this.refreshRecents();
  }

  public close(): void {
    this.isOpen.set(false);
    this.recentsRequestId += 1;
    this.recents.set([]);
    this.reset();
  }

  public toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  public setQuery(query: string): void {
    this.query.set(query);
    this.queryInput.next(query);
  }

  public async select(result: UniversalSearchResult): Promise<void> {
    this.rememberRecent(result);
    this.close();
    await this.router.navigate(result.commands);
  }

  public async goTo(commands: unknown[]): Promise<void> {
    this.close();
    await this.router.navigate(commands);
  }

  private reset(): void {
    this.latestRequestId += 1;
    this.query.set('');
    this.workspaceResults.set([]);
    this.truncated.set(false);
    this.status.set('idle');
  }

  private async searchWorkspace(query: string): Promise<void> {
    const workspaceId = this.currentWorkspaceId();

    if (!workspaceId || !isRemoteSearchQuery(query)) {
      this.latestRequestId += 1;
      this.workspaceResults.set([]);
      this.truncated.set(false);
      this.status.set('idle');
      return;
    }

    const requestId = ++this.latestRequestId;
    this.status.set('loading');

    try {
      const response = await this.api.search(workspaceId, query);
      if (requestId !== this.latestRequestId) return;
      this.workspaceResults.set(this.toResults(workspaceId, response.results));
      this.truncated.set(response.truncated);
      this.status.set('ready');
    } catch {
      if (requestId !== this.latestRequestId) return;
      this.workspaceResults.set([]);
      this.truncated.set(false);
      this.status.set('error');
    }
  }

  private toResults(workspaceId: string, hits: WorkspaceSearchHit[]): UniversalSearchResult[] {
    return hits.flatMap((hit) => {
      const commands = this.commandsForHit(workspaceId, hit);
      if (!commands) return [];
      return [
        {
          kind: hit.kind as UniversalSearchResult['kind'],
          id: hit.document_id,
          title: hit.title,
          subtitle: hit.subtitle,
          commands,
        },
      ];
    });
  }

  /**
   * Builds a Web route from the identifiers the backend returned. Hits missing an identifier the
   * route needs are dropped rather than routed to a broken URL.
   */
  private commandsForHit(workspaceId: string, hit: WorkspaceSearchHit): unknown[] | null {
    const workspacePath = ['/workspace', workspaceId];
    const managePath = [...workspacePath, 'manage'];
    const params = hit.route_params ?? {};

    switch (hit.kind) {
      case 'folder':
        return params['folder_id'] ? [...workspacePath, 'folders', params['folder_id'], 'inspections'] : null;
      case 'inspection':
        return params['folder_id'] && params['inspection_id'] ? [...workspacePath, 'folders', params['folder_id'], 'inspections', params['inspection_id'], 'overview'] : null;
      case 'client':
        return params['client_id'] ? [...managePath, 'clients', params['client_id']] : null;
      case 'site':
        return params['site_id'] ? [...managePath, 'sites', params['site_id']] : null;
      case 'member':
        return params['member_id'] ? [...managePath, 'members', params['member_id']] : null;
      case 'role':
        return params['role_id'] ? [...managePath, 'roles', params['role_id']] : null;
      case 'assignee':
        return params['assignee_id'] ? [...managePath, 'assignees', params['assignee_id']] : null;
      case 'template':
        return params['template_id'] ? [...managePath, 'templates', params['template_id'], 'overview'] : null;
      case 'theme':
        return params['theme_id'] ? [...managePath, 'themes', params['theme_id'], 'editor'] : null;
      default:
        return null;
    }
  }

  private rememberRecent(result: UniversalSearchResult): void {
    const workspaceId = this.currentWorkspaceId();
    if (!workspaceId) return;

    const recents = [result, ...this.readRecents().filter((recent) => !(recent.kind === result.kind && recent.id === result.id))].slice(0, MAX_RECENTS);
    this.recents.set(recents);
    this.writeRecents(workspaceId, recents);
  }

  /**
   * Recents are stored on the device. Deleted or inaccessible records are dropped before display
   * and removed from localStorage so they do not keep reappearing.
   */
  private async refreshRecents(): Promise<void> {
    const workspaceId = this.currentWorkspaceId();
    const requestId = ++this.recentsRequestId;
    const stored = this.readRecents();

    if (!workspaceId || stored.length === 0) {
      if (requestId === this.recentsRequestId) this.recents.set([]);
      return;
    }

    const { recents, pruned } = await this.availabilityService.filterExisting(workspaceId, stored);
    if (requestId !== this.recentsRequestId) return;

    this.recents.set(recents);
    if (pruned) this.writeRecents(workspaceId, recents);
  }

  private readRecents(): UniversalSearchResult[] {
    const workspaceId = this.currentWorkspaceId();
    if (!workspaceId) return [];

    try {
      const stored = localStorage.getItem(this.recentsKey(workspaceId));
      if (!stored) return [];
      const parsed: unknown = JSON.parse(stored);
      return Array.isArray(parsed) ? (parsed as UniversalSearchResult[]) : [];
    } catch {
      return [];
    }
  }

  private writeRecents(workspaceId: string, recents: UniversalSearchResult[]): void {
    try {
      localStorage.setItem(this.recentsKey(workspaceId), JSON.stringify(recents));
    } catch {
      // Storage is unavailable (private browsing, quota); recents are a convenience only.
    }
  }

  private recentsKey(workspaceId: string): string {
    return `${RECENTS_STORAGE_PREFIX}.${workspaceId}`;
  }

  private handleNavigation(url: string): void {
    const previousWorkspaceId = this.currentWorkspaceId();
    this.routeUrl.set(url);
    if (this.isOpen()) {
      this.close();
      return;
    }

    if (previousWorkspaceId === this.currentWorkspaceId()) return;

    this.latestRequestId += 1;
    this.recentsRequestId += 1;
    this.workspaceResults.set([]);
    this.truncated.set(false);
    this.status.set('idle');
    this.recents.set([]);
  }

  private workspaceIdFromUrl(url: string): string | null {
    return url.match(/^\/workspace\/([^/?#]+)(?:[/?#]|$)/)?.[1] ?? null;
  }
}
